import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Record, useDataProvider } from 'react-admin';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SnackbarContainer } from 'components/standalone';
import { AppRole } from 'providers/roles';
import { extractErrorMessage } from 'utils';
import { UserContainer, UserFormValues } from '../containers';
import { UserForm } from '../components';

type LocationState = {
  formState: Record;
  selectedBumpers: string[];
};

const UserCreateInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dataProvider = useDataProvider();
  const { notify } = SnackbarContainer.useContainer();
  const { formLoading, loadRoleOptions, loadRegionOptions, setFormLoading } =
    UserContainer.useContainer();

  const searchParams = new URLSearchParams(location.search);
  const contractorId = searchParams.get('contractorId');

  const routerState = location.state as LocationState | undefined;
  const formState = routerState?.formState || null;
  const selectedBumpers = routerState?.selectedBumpers || null;

  const handleSubmit = useCallback(
    async (values: UserFormValues) => {
      setFormLoading(true);
      try {
        const userResult = await dataProvider.create('user', {
          data: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            primaryPhone: values.primaryPhone,
            idNumber: values.idNumber,
            password: values.password,
            active: values.active,
          },
        });
        const user = userResult.data;

        // Regions before roles: the API grants the RegionalAdmin role only to a user that already has a Region.
        await Promise.all(
          values.selectedRegions.map(regionId =>
            dataProvider.create('user-region', {
              data: {
                userId: user.id,
                regionId,
              },
            }),
          ),
        );

        await Promise.all(
          values.selectedRoles.map(roleId =>
            dataProvider.create('user-role', {
              data: {
                userId: user.id,
                roleId,
              },
            }),
          ),
        );

        if (!contractorId) {
          history.push('/user');
          notify('Element created');
          return;
        }

        // Redirect to user's edit page if this create form
        // was originated from a contractor's edit flow
        history.push({
          pathname: `/user/${user.id}`,
          search: `contractorId=${contractorId}`,
          state: {
            _scrollToTop: true,
            user,
            formState,
            selectedBumpers,
          },
        });
      } catch (error: unknown) {
        console.error('User create failed:', error);
        notify(
          extractErrorMessage(error, 'Error while creating the user'),
          'error',
        );
      } finally {
        setFormLoading(false);
      }
    },
    [
      dataProvider,
      contractorId,
      formState,
      selectedBumpers,
      history,
      notify,
      setFormLoading,
    ],
  );

  const handleGoBack = useCallback(() => {
    history.push('/user');
  }, [history]);

  return (
    <Box className={classes.createBox}>
      <Box className={classes.formContainer}>
        <UserForm
          mode="create"
          onSubmit={handleSubmit}
          loading={formLoading}
          onGoBack={handleGoBack}
          loadRoleOptions={loadRoleOptions}
          loadRegionOptions={loadRegionOptions}
          preselectedRoleName={contractorId ? AppRole.Bumper : undefined}
          rolesDisabled={!!contractorId}
        />
      </Box>
    </Box>
  );
};

export const UserCreate = () => {
  return (
    <UserContainer.Provider>
      <UserCreateInner />
    </UserContainer.Provider>
  );
};

const useStyles = makeStyles(theme => ({
  createBox: {},
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
}));

export default UserCreate;
